// Content-based dedupe for the pull pass. Multi-device scenario:
// Device A creates a row offline (clientId = X). Device B creates the SAME
// logical row offline before either has signed in (clientId = Y because each
// device's Dexie `creating` hook stamps a fresh UUID). After both sync, the
// server has two rows for one logical entry because the UNIQUE constraint is
// `(owner_id, client_id)`, not on the content key.
//
// Fix: on the FIRST pull after sign-in (cursor still unset), before the
// upsert applies the incoming server row by clientId, look for a local
// Dexie row with the same CONTENT key but a different clientId. If found,
// rewrite the local row's clientId to match the server's. The subsequent
// upsert then collides naturally and last-write-wins picks the winner.
//
// Why only on first sync: after the device has successfully pulled even
// once, every locally-known clientId is either an echo from the server or
// a fresh post-sign-in creation that intentionally represents a NEW logical
// row. Running content dedupe on every pull would silently collapse the
// "user manually added a duplicate" case into one row, hiding data.
//
// Why only on live (non-deleted) remotes: if a server tombstone arrives and
// matches by content, the local row might be a legitimate post-delete
// re-creation. Cascading the tombstone via content would discard the
// user's new entry. The orchestrator already deletes by clientId match;
// content cascade is intentionally NOT implemented (called out as optional
// in the spec).
//
// We only dedupe tables with a clean natural key:
//   - check_ins: (owner_id, entry_date YYYY-MM-DD, type) — at most one
//     morning/noon/evening per day per owner is the product invariant.
//   - tenth_steps: (owner_id, entry_date YYYY-MM-DD) — one nightly review
//     per day per owner.
//
// sponsor_tasks intentionally has no dedupe key: titles can be edited, due
// dates are optional, and recurring tasks create near-duplicates. We accept
// possible duplicate sponsor_tasks across devices; the user can delete the
// extra from the UI. See sponsorTasksSync.ts for the parallel comment.

import { format } from 'date-fns';
import { db } from '../db';

export function checkInContentKey(
  entryDate: string,
  type: 'morning' | 'noon' | 'evening',
): string {
  return `${entryDate}::${type}`;
}

export function tenthStepContentKey(entryDate: string): string {
  return entryDate;
}

// Rewrites the local check-in row's clientId to match the remote's, IF a
// content-match exists with a different clientId AND that local row's
// clientId is NOT itself present in the current incoming batch.
//
// Why the `incomingClientIds` filter matters: if local rows L1 (cid=X) and
// L2 (cid=Y) both exist on this device for the same (day, type) — e.g.
// because they were pushed up together on a prior sync — then a later pull
// returns both X and Y. Without the filter, processing remote Y would
// match local L1 (cid=X, content match) and rewrite L1's clientId to Y,
// collapsing two legitimate rows into one. Excluding X from the candidate
// pool because X is itself in this batch preserves the (rare) two-rows-
// per-day case while still catching the genuine offline-on-both-devices
// duplication.
//
// Idempotent — if no local row needs rewriting, this is a no-op. Returns
// true if a rewrite happened.
//
// Note: this MUST run inside the same `db.transaction('rw', db.checkIns)`
// block as the caller's upsert, otherwise the upsert's subsequent
// `where('clientId').equals(remote.client_id).first()` lookup races with
// this update. Callers thread the transaction through.
export async function dedupeCheckInByContent(
  remoteClientId: string,
  remoteEntryDate: string,
  remoteType: 'morning' | 'noon' | 'evening',
  incomingClientIds: ReadonlySet<string>,
): Promise<boolean> {
  // If a local row with the remote's clientId already exists, the natural
  // upsert path will handle it — no dedupe needed.
  const byClientId = await db.checkIns
    .where('clientId')
    .equals(remoteClientId)
    .first();
  if (byClientId) return false;

  // Indexed scan on `type`, then in-memory filter by date + clientId.
  const candidates = await db.checkIns
    .where('type')
    .equals(remoteType)
    .toArray();
  const match = candidates.find((row) => {
    if (!row.clientId || row.clientId === remoteClientId) return false;
    if (incomingClientIds.has(row.clientId)) return false;
    return format(row.date, 'yyyy-MM-dd') === remoteEntryDate;
  });
  if (match?.id == null) return false;

  await db.checkIns.update(match.id, { clientId: remoteClientId });
  return true;
}

export async function dedupeTenthStepByContent(
  remoteClientId: string,
  remoteEntryDate: string,
  incomingClientIds: ReadonlySet<string>,
): Promise<boolean> {
  const byClientId = await db.tenthSteps
    .where('clientId')
    .equals(remoteClientId)
    .first();
  if (byClientId) return false;

  const candidates = await db.tenthSteps.toArray();
  const match = candidates.find((row) => {
    if (!row.clientId || row.clientId === remoteClientId) return false;
    if (incomingClientIds.has(row.clientId)) return false;
    return format(row.date, 'yyyy-MM-dd') === remoteEntryDate;
  });
  if (match?.id == null) return false;

  await db.tenthSteps.update(match.id, { clientId: remoteClientId });
  return true;
}
